import {
  createContext,
  useContext,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Maximize2 } from 'lucide-react';
import { Modal } from '@ai-tutor/ui';

/**
 * Whether this is already inside an `Enlargeable` - its picture, or its
 * dialog. A figure drawn inside one (a concept diagram: VisualBlock wraps it,
 * and it draws through the markdown pipeline, which offers its own) must not
 * add a second button - two stacked in one corner, each opening its own
 * dialog - nor one inside the dialog that already shows it full size.
 */
const EnlargeableContext = createContext(false);

export function useInsideEnlargeable(): boolean {
  return useContext(EnlargeableContext);
}

/** For a control that opens a figure full size itself: the figures drawn
 *  inside it - its preview and its dialog - add no enlarge button of their
 *  own (a button inside a button, or inside the dialog that is the enlarged
 *  view). */
export function AlreadyEnlargeable({ children }: { children: ReactNode }) {
  return (
    <EnlargeableContext.Provider value={true}>
      {children}
    </EnlargeableContext.Provider>
  );
}

/**
 * A picture that opens full size. In a slim column (the chat panel, a phone)
 * a figure is drawn at ~220px and its labels are hard to read: a tap on the
 * picture, or its corner button (the keyboard's way in), shows `large` in a
 * dialog. Clicks on the figure's own controls (the caption's info button, a
 * link) are left to them.
 *
 * `buttonOnly` is the card's way in. On a flashcard or a quiz, a click on the
 * card already flips or answers it, so the figure itself must not be a target
 * and the button must not let the click reach the card behind it.
 */
export function Enlargeable({
  label,
  large,
  buttonOnly = false,
  children,
}: {
  /** What the picture shows: names the button and the dialog. */
  label: string;
  /** The picture drawn big, for the dialog. */
  large: ReactNode;
  /** Only the corner button opens it, and the click stops there. */
  buttonOnly?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const onPicture = (e: MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, a')) return;
    setOpen(true);
  };
  return (
    <EnlargeableContext.Provider value={true}>
      <div className='group relative' data-enlargeable>
        {buttonOnly ? (
          children
        ) : (
          <>
            {/* The button below is the keyboard's way in; this is for a pointer. */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div className='cursor-zoom-in' onClick={onPicture}>
              {children}
            </div>
          </>
        )}
        <button
          type='button'
          onClick={e => {
            // A card's own click handler (flip / answer) sits above this.
            e.stopPropagation();
            setOpen(true);
          }}
          aria-label={`Enlarge: ${label}`}
          className='pointer-events-auto absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white/90 text-slate-700 opacity-80 shadow-sm transition-opacity hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-100'
        >
          <Maximize2 className='h-4 w-4' aria-hidden='true' />
        </button>
        <Modal
          isOpen={open}
          onClose={() => setOpen(false)}
          ariaLabel={label}
          size='xl'
        >
          <div className='max-h-[80dvh] overflow-y-auto'>{large}</div>
        </Modal>
      </div>
    </EnlargeableContext.Provider>
  );
}
